/**
 * @file recently-viewed-section.js
 * Hydrates the recently-viewed section with products from localStorage
 * using the Predictive Search API + Section Rendering API.
 */

import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Returns the current product ID if we're on a PDP, otherwise null.
 * Looks for a `[data-product-id]` ancestor of the section element.
 * @param {HTMLElement} element
 * @returns {string|null}
 */
function getCurrentProductId(element) {
  return (/** @type {HTMLElement|null} */ (element.closest('[data-product-id]')))?.dataset.productId ?? null;
}

/**
 * Builds the Predictive Search URL for a given set of product IDs.
 * Shopify populates `predictive_search` in Liquid when a section is
 * rendered via this endpoint.
 * @param {string[]} ids
 * @param {number} limit
 * @returns {URL}
 */
function buildSearchUrl(ids, limit) {
  const url = new URL(Theme.routes.predictive_search_url, location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', String(limit));
  return url;
}

// ── Custom Element ────────────────────────────────────────────────────────

class RecentlyViewedSection extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {
    // Skip if already server-rendered with products (no hydration needed)
    if (!this.dataset.pendingHydration) return;
    this.#hydrate();
  }

  disconnectedCallback() {
    this.#abort();
  }

  // ── Private ─────────────────────────────────────────────────────────────

  #abort() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Resolves the product IDs to display, excluding the current product
   * on PDP pages and capping at the section's configured limit.
   * @param {number} limit
   * @returns {string[]}
   */
  #resolveProductIds(limit) {
    const currentId = getCurrentProductId(this);
    return RecentlyViewed.getProducts()
      .filter((id) => id !== currentId)
      .slice(0, limit);
  }

  /**
   * Fetches the section HTML rendered with the resolved product IDs
   * via the Predictive Search + Section Rendering APIs.
   * @param {string[]} ids
   * @param {number} limit
   * @returns {Promise<string | null>}
   */
  async #fetchSectionHTML(ids, limit) {
    const url = buildSearchUrl(ids, limit);
    if (!this.dataset.sectionId) return null;
    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /** Orchestrates the full hydration flow. */
  async #hydrate() {
    const { sectionId, maxProducts } = this.dataset;
    if (!sectionId) return;

    const limit = parseInt(maxProducts ?? '4', 10);
    const ids = this.#resolveProductIds(limit);

    // Nothing in localStorage — keep section hidden
    if (ids.length === 0) return;

    this.#abort();
    this.#abortController = new AbortController();

    try {
      const html = await this.#fetchSectionHTML(ids, limit);

      if (!html || this.#abortController.signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Extract only the recently-viewed block from the fetched HTML
      const recentlyViewedContent = doc.querySelector('recently-viewed-section');

      if (recentlyViewedContent) {
        this.innerHTML = recentlyViewedContent.innerHTML;
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[recently-viewed-section]', error);
      }
    }
  }
}

customElements.define('recently-viewed-section', RecentlyViewedSection);